import { create } from 'zustand';
import {
  TodayLayoutPreferences,
  TodayLayoutData,
  TodayLayoutItem,
  PreferenceContextEnum,
  isTodayLayoutPreferences,
  isTodayLayoutData,
  isTodayLegacyLayoutData,
} from '@scrambl/shared';
import { useUserPreferencesStore } from '@/store/user-preferences.store';
import { useOrganizationStore } from '@/store/organization.store';
import { useAuth as useAuthStore } from '@/store/auth.store';
import { WIDGET_DEFINITIONS } from '@/lib/widgets/widget-definitions';
import { AccountType, ModuleTypeEnum } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';

const logger = createLogger('store/today-layout.store');

const SIZE_ORDER: TodayLayoutItem['size'][] = ['small', 'medium', 'large'];

const getDefaultSizeForWidget = (widgetId: string): TodayLayoutItem['size'] => {
  const def = WIDGET_DEFINITIONS.find((w) => w.id === widgetId);
  if (def?.defaultSize) return def.defaultSize;
  return def?.columnType === 'sidebar' ? 'small' : 'large';
};

const clampToMinSize = (
  widgetId: string,
  size: TodayLayoutItem['size'],
): TodayLayoutItem['size'] => {
  const def = WIDGET_DEFINITIONS.find((w) => w.id === widgetId);
  const minIdx = SIZE_ORDER.indexOf(def?.minSize ?? 'small');
  const maxIdx = SIZE_ORDER.indexOf(def?.maxSize ?? 'large');
  const sizeIdx = SIZE_ORDER.indexOf(size);
  if (sizeIdx < minIdx) return SIZE_ORDER[minIdx];
  if (sizeIdx > maxIdx) return SIZE_ORDER[maxIdx];
  return size;
};

const GRID_COLUMNS = 3;
const sizeToCols = (size: TodayLayoutItem['size']): number =>
  size === 'small' ? 1 : size === 'medium' ? 2 : 3;

/**
 * Reorder a list of widgets so each row of the 3-column grid is filled as fully
 * as possible. Walks the queue in order; for each open row, prefers an exact-fit
 * widget over a first-fit one. Used during migration / initial-default generation
 * so users don't land on a layout with avoidable gaps. Does NOT run on
 * already-customized layouts — the user's chosen order is respected there.
 */
const packTightly = (items: TodayLayoutItem[]): TodayLayoutItem[] => {
  const queue = [...items];
  const result: TodayLayoutItem[] = [];

  while (queue.length > 0) {
    let rowSpace = GRID_COLUMNS;

    while (rowSpace > 0 && queue.length > 0) {
      // Prefer a widget that fills the remaining row exactly; otherwise the first that fits.
      let pickIdx = queue.findIndex((i) => sizeToCols(i.size) === rowSpace);
      if (pickIdx === -1) {
        pickIdx = queue.findIndex((i) => sizeToCols(i.size) <= rowSpace);
      }
      if (pickIdx === -1) break; // nothing fits — wrap to next row

      const [picked] = queue.splice(pickIdx, 1);
      result.push(picked);
      rowSpace -= sizeToCols(picked.size);
    }
  }

  return result;
};

// A widget belongs in an account type's default layout when it has no
// accountTypes gate (all accounts) or explicitly lists this account type. This
// keeps business widgets out of a personal default and vice-versa.
const isWidgetForAccountType = (
  w: (typeof WIDGET_DEFINITIONS)[number],
  accountType: AccountType,
): boolean => !w.accountTypes || w.accountTypes.includes(accountType);

const generateDefaultLayout = (
  accountType: AccountType = AccountType.ORGANIZATION,
): TodayLayoutData => {
  const items: TodayLayoutItem[] = [];
  for (const w of WIDGET_DEFINITIONS) {
    if (
      w.isDefault &&
      w.columnType === 'main' &&
      isWidgetForAccountType(w, accountType)
    ) {
      items.push({ id: w.id, size: getDefaultSizeForWidget(w.id) });
    }
  }
  for (const w of WIDGET_DEFINITIONS) {
    if (
      w.isDefault &&
      w.columnType === 'sidebar' &&
      isWidgetForAccountType(w, accountType)
    ) {
      items.push({ id: w.id, size: getDefaultSizeForWidget(w.id) });
    }
  }
  return { items: packTightly(items) };
};

// Read the current account type at call time (store, not a hook). Defaults to
// ORGANIZATION when the org hasn't loaded — fail safe toward the full experience.
const getCurrentAccountType = (): AccountType =>
  (useOrganizationStore.getState().currentOrganization?.accountType as
    | AccountType
    | undefined) ?? AccountType.ORGANIZATION;

// Account-type-aware default computed fresh so a personal space never falls back
// to a layout carrying business widgets (or vice-versa).
const getDefaultLayout = (): TodayLayoutData =>
  generateDefaultLayout(getCurrentAccountType());

const migrateLayout = (raw: unknown): TodayLayoutData | null => {
  if (isTodayLayoutData(raw)) {
    // Already in the new shape — respect the user's chosen order, only fix up sizes.
    return {
      items: raw.items
        .filter((i) => typeof i?.id === 'string')
        .map((i) => ({
          id: i.id,
          size: clampToMinSize(i.id, i.size ?? getDefaultSizeForWidget(i.id)),
        })),
    };
  }
  if (isTodayLegacyLayoutData(raw)) {
    // Coming from the old two-column shape — pack tightly so the user
    // lands on a clean grid without avoidable gaps.
    const items: TodayLayoutItem[] = [
      ...raw.main.map((id) => ({ id, size: clampToMinSize(id, getDefaultSizeForWidget(id)) })),
      ...raw.sidebar.map((id) => ({ id, size: clampToMinSize(id, getDefaultSizeForWidget(id)) })),
    ];
    return { items: packTightly(items) };
  }
  return null;
};

/* ===== Last-known layout snapshot =====
 *
 * The widget grid can't render at all until the layout resolves, and the
 * layout lives behind the user-preferences fetch — so even with every widget
 * query restored from cache, a cold open is a full-page skeleton for as long
 * as that round trip takes. Keeping the last-known layout in localStorage lets
 * the grid paint immediately and swap in the server layout when it lands.
 *
 * Keyed by user AND org: the layout differs per org, and an org switch must
 * never flash the previous org's widget set. Capped at 24h so an abandoned
 * machine doesn't resurrect a long-dead dashboard.
 */
const SNAPSHOT_KEY_PREFIX = 'scrambl:today-layout:';
const SNAPSHOT_MAX_AGE_MS = 1000 * 60 * 60 * 24;

interface TodayLayoutSnapshot {
  storedAt: number;
  layout: TodayLayoutData;
}

const getSnapshotKey = (): string | null => {
  if (typeof window === 'undefined') return null;
  const userId = useAuthStore.getState().user?.id;
  const orgId = useOrganizationStore.getState().currentOrganization?.id;
  if (!userId || !orgId) return null;
  return `${SNAPSHOT_KEY_PREFIX}${userId}:${orgId}`;
};

/** Last-known layout for the current user+org, or null when there is none / it aged out. */
export const readTodayLayoutSnapshot = (): TodayLayoutData | null => {
  const key = getSnapshotKey();
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TodayLayoutSnapshot;
    if (
      typeof parsed?.storedAt !== 'number' ||
      Date.now() - parsed.storedAt > SNAPSHOT_MAX_AGE_MS
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    // Run it through the same migration as a server payload — the widget
    // catalogue may have changed shape since the snapshot was written.
    const migrated = migrateLayout(parsed.layout);
    return migrated && migrated.items.length > 0 ? migrated : null;
  } catch {
    return null;
  }
};

const writeTodayLayoutSnapshot = (layout: TodayLayoutData): void => {
  const key = getSnapshotKey();
  if (!key || layout.items.length === 0) return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ storedAt: Date.now(), layout } satisfies TodayLayoutSnapshot),
    );
  } catch {
    // Quota / private mode — the snapshot is best-effort, never load-bearing.
  }
};

/** Drop every stored layout snapshot. Called at sign-out, alongside the other
 *  persisted-store cleanup, so the next account on this machine starts clean. */
export const clearTodayLayoutSnapshots = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(SNAPSHOT_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Best-effort.
  }
};

interface TodayLayoutState {
  layout: TodayLayoutData | null;
  isLoading: boolean;
  error: Error | null;
  fetchLayout: () => Promise<void>;
  saveLayout: (newLayoutConfig: TodayLayoutData) => Promise<void>;
  reset: () => void;
}

let isFetchingLayout = false;

export const useTodayLayout = create<TodayLayoutState>((set, get) => ({
  layout: null,
  isLoading: false,
  error: null,
  fetchLayout: async () => {
    if (isFetchingLayout) return;

    isFetchingLayout = true;

    // Paint the last-known grid immediately and let the real fetch revalidate
    // behind it. Only seeds when we have nothing — never overwrites a layout
    // already in memory.
    const snapshot = get().layout ? null : readTodayLayoutSnapshot();
    set({ isLoading: true, error: null, ...(snapshot ? { layout: snapshot } : {}) });

    try {
      const waitForPreferences = async (maxAttempts = 10, interval = 200): Promise<boolean> => {
        for (let i = 0; i < maxAttempts; i++) {
          const userPrefsStore = useUserPreferencesStore.getState();
          if (userPrefsStore.isInitialized && !userPrefsStore.isLoading) {
            return true;
          }
          if (i < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, interval));
          }
        }
        return false;
      };

      const userPrefsStore = useUserPreferencesStore.getState();
      if (!userPrefsStore.isInitialized || userPrefsStore.isLoading) {
        const initialized = await waitForPreferences();
        if (!initialized) {
          set({ layout: getDefaultLayout(), isLoading: false });
          isFetchingLayout = false;
          return;
        }
      }

      const currentPrefsStore = useUserPreferencesStore.getState();
      const userPrefs = currentPrefsStore.getPreference(
        PreferenceContextEnum.LAYOUT,
        ModuleTypeEnum.TODAY,
      );

      // Only layouts that came back from a *resolved* preferences fetch are
      // snapshotted. The timeout/catch fallbacks below deliberately aren't —
      // they'd overwrite a good snapshot with a generic default grid.
      if (userPrefs?.preferences && isTodayLayoutPreferences(userPrefs.preferences)) {
        const migrated = migrateLayout(userPrefs.preferences.layout);
        if (!migrated || migrated.items.length === 0) {
          const fallback = getDefaultLayout();
          writeTodayLayoutSnapshot(fallback);
          set({ layout: fallback, isLoading: false });
        } else {
          writeTodayLayoutSnapshot(migrated);
          set({ layout: migrated, isLoading: false });
        }
      } else {
        const defaults = getDefaultLayout();
        writeTodayLayoutSnapshot(defaults);
        set({ layout: defaults, isLoading: false });
      }
    } catch (e) {
      logger.error('TodayLayoutStore: Error fetching layout:', e);
      set({ error: e as Error, isLoading: false, layout: get().layout || getDefaultLayout() });
    } finally {
      isFetchingLayout = false;
    }
  },
  saveLayout: async (newLayoutConfig) => {
    set({ error: null });
    try {
      const preferencesToSave = { layout: newLayoutConfig } as Record<string, unknown>;
      const updatedUserPreferenceData = await useUserPreferencesStore
        .getState()
        .savePreference(PreferenceContextEnum.LAYOUT, preferencesToSave, ModuleTypeEnum.TODAY);

      if (
        updatedUserPreferenceData?.preferences &&
        isTodayLayoutPreferences(updatedUserPreferenceData.preferences)
      ) {
        const migrated = migrateLayout(updatedUserPreferenceData.preferences.layout);
        writeTodayLayoutSnapshot(migrated ?? newLayoutConfig);
        set({ layout: migrated ?? newLayoutConfig });
      } else {
        logger.warn('TodayLayoutStore: Save layout response was not as expected.');
        writeTodayLayoutSnapshot(newLayoutConfig);
        set({
          layout: newLayoutConfig,
          error: new Error('Layout saved, but response was unexpected.'),
        });
      }
    } catch (e) {
      logger.error('TodayLayoutStore: Error saving layout:', e);
      set({ error: e as Error });
    }
  },
  reset: () => {
    isFetchingLayout = false;
    set({ layout: null, isLoading: false, error: null });
  },
}));

// Subscribe to user preferences changes to sync today layout
let lastPreferencesUpdateTimestamp = 0;
useUserPreferencesStore.subscribe((state) => {
  const todayPref = state.preferences.find(
    (p) => p.context === PreferenceContextEnum.LAYOUT && p.moduleType === ModuleTypeEnum.TODAY,
  );

  if (todayPref?.updatedAt) {
    const updateTime = new Date(todayPref.updatedAt).getTime();
    if (updateTime > lastPreferencesUpdateTimestamp) {
      lastPreferencesUpdateTimestamp = updateTime;

      if (todayPref.preferences && isTodayLayoutPreferences(todayPref.preferences)) {
        const migrated = migrateLayout(todayPref.preferences.layout);
        if (!migrated) return;
        const currentLayout = useTodayLayout.getState().layout;
        if (JSON.stringify(currentLayout) !== JSON.stringify(migrated)) {
          writeTodayLayoutSnapshot(migrated);
          useTodayLayout.setState({ layout: migrated });
        }
      }
    }
  }
});

export type { TodayLayoutData, TodayLayoutItem };
